// ติดตั้ง Leaflet ก่อน (ผ่าน npm)
// npm install leaflet

import React, { useEffect, useState } from 'react';
import { ref, get } from 'firebase/database';
import { database } from './firebaseConfig';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

ChartJS.register(ArcElement, Tooltip, Legend);

// แก้ปัญหา icon หายบน Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

function EarthquakeReportPage() {
  const [items, setItems] = useState([]);
  const [selectedSources, setSelectedSources] = useState({ EMSC: true, USGS: false, TMD: false, shake: false });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    const fetchData = async () => {
      let allItems = [];

      for (const source of Object.keys(selectedSources)) {
        if (selectedSources[source]) {
          const sourceRef = ref(database, `earthquake_history/${source}`);
          const snapshot = await get(sourceRef);

          if (snapshot.exists()) {
            const data = snapshot.val();
            const parsed = Object.values(data).map((item) => {
              const region = item.region || '-';
              const distance = item.distance_from_bkk_km || item.distance || '-';             
              const magnitude = item.magnitude || item.mag || '-';
              const utc = item.time_utc_str || item.utc || '-';
              const lat = item.lat;
              const lon = item.lon;

              const { date, time } = convertUtcToThai(utc, source);
              return { region, distance, magnitude, date, time, source, lat, lon };
            });
            allItems = allItems.concat(parsed);
          }
        }
      }

      const filtered = allItems.filter(item => item.date === formatThaiDate(selectedDate));
      filtered.sort((a, b) => b.time.localeCompare(a.time));
      setItems(filtered);
    };

    fetchData();
  }, [selectedSources, selectedDate]);

  const handleCheckboxChange = (source) => {
    setSelectedSources((prev) => ({ ...prev, [source]: !prev[source] }));
  };

  const getDisplayName = (sourceKey) => {
  const mapping = {
    EMSC: 'EMSC',
    USGS: 'USGS',
    TMD: 'TMD',
    shake: 'InSAR'
  };
  return mapping[sourceKey] || sourceKey;
};


  const convertUtcToThai = (utcStr, source) => {
    let date = new Date(utcStr);
    if (isNaN(date)) return { date: '-', time: '-' };
    if (source === "shake") date = new Date(date.getTime() + 7 * 60 * 60 * 1000);
    return {
      date: date.toLocaleDateString("th-TH", { timeZone: "Asia/Bangkok" }),
      time: date.toLocaleTimeString("th-TH", { timeZone: "Asia/Bangkok", hour12: false })
    };
  };

  const formatThaiDate = (isoStr) => {
    const d = new Date(isoStr);
    return d.toLocaleDateString("th-TH", { timeZone: "Asia/Bangkok" });
  };

  const getPieChartData = () => {
    const ranges = { '1.0–1.9': 0, '2.0–2.9': 0, '3.0–3.9': 0, '4.0–4.9': 0, '5.0+': 0 };
    items.forEach(({ magnitude }) => {
      const mag = parseFloat(magnitude);
      if (isNaN(mag)) return;
      if (mag < 2) ranges['1.0–1.9']++;
      else if (mag < 3) ranges['2.0–2.9']++;
      else if (mag < 4) ranges['3.0–3.9']++;
      else if (mag < 5) ranges['4.0–4.9']++;
      else ranges['5.0+']++;
    });
    return {
      labels: Object.keys(ranges),
      datasets: [{ data: Object.values(ranges), backgroundColor: ['#4caf50', '#2196f3', '#ffc107', '#ff5722', '#9c27b0'] }]
    };
  };

  return (
    <div className="container mt-5">
      <div className="card shadow-lg">
        <div className="card-body">
          <h4 className="mb-3">รายงานแผ่นดินไหวย้อนหลัง </h4>

          <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
            {Object.keys(selectedSources).map((source) => (
              <div key={source} className="form-check form-check-inline">
                <input type="checkbox" className="form-check-input" id={`chk-${source}`} checked={selectedSources[source]} onChange={() => handleCheckboxChange(source)} />
                <label className="form-check-label" htmlFor={`chk-${source}`}>{getDisplayName(source)}</label>

              </div>
            ))}
            <input type="date" className="form-control w-auto" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr><th>Region</th><th>Magnitude</th><th>เวลา</th><th>ระยะห่าง (กม.)</th><th>วันที่</th><th>แหล่ง</th></tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.region}</td>
                    <td>{item.magnitude}</td>
                    <td>{item.time}</td>
                    <td>{item.distance}</td>
                    <td>{item.date}</td>                    
                    <td>{getDisplayName(item.source)}</td>

                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan="6" className="text-center text-muted">ยังไม่มีข้อมูลที่เลือก</td></tr>}
              </tbody>
            </table>
          </div>

          {/* แผนที่ Leaflet */}
          <div className="mt-5">
            <h5 className="text-center">ตำแหน่งที่เกิดแผ่นดินไหว</h5>
            <MapContainer center={[15, 100]} zoom={5} style={{ height: '400px', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {items.map((item, idx) => (
                item.lat && item.lon && (
                  <Marker key={idx} position={[item.lat, item.lon]}>
                    <Popup>
                      <strong>{item.region}</strong><br />
                      Mag: {item.magnitude}<br />
                      เวลา: {item.time}
                    </Popup>
                  </Marker>
                )
              ))}
            </MapContainer>
          </div>

          <div className="mt-5">
            <h5 className="text-center mb-3">สรุประดับ Magnitude ({formatThaiDate(selectedDate)})</h5>
            <div className="d-flex justify-content-center">
              <div style={{ maxWidth: '400px', width: '100%' }}>
                <Pie data={getPieChartData()} />
              </div>
            </div>
          </div>

          

        </div>
      </div>
    </div>
  );
}

export default EarthquakeReportPage;
